import {
  BundleLineVariant,
  BundleLineVariantId,
  Category,
  ImageAsset,
  Product,
} from "@greeter/core";
import { useIndexBy } from "./hooks";
import { Thumb } from "./Thumb";
import { formatMoney } from "@greeter/formatters";
import { dropInOutFromBottom, staggerForwardsBackwards } from "@greeter/motion";
import { Card, CardProps } from "@greeter/matter-v2";

import css from "./VariantLibraryPicker.module.scss";

export type PriceDisplay = "salesPrice" | "additionalPrice";

type VariantCardProps = {
  variant: BundleLineVariant;
  thumbUrl?: string;
  categoryName?: string;
  priceDisplay?: PriceDisplay;
} & Omit<CardProps, "variant">;

function VariantCard({ variant, ...props }: VariantCardProps) {
  props.priceDisplay ??= "additionalPrice";

  return (
    <Card
      className={css.VariantCard}
      {...props}
      data-testid={`variant-${variant.id}`}
    >
      <Card.Content className={css.VariantCardContent}>
        {props.thumbUrl && <Thumb className={css.Thumb} src={props.thumbUrl} />}
        <div className={css.VariantCardInfo}>
          <span className={css.VariantCardInfoTitle}>
            {variant.name}

            {props.priceDisplay === "additionalPrice" &&
              variant.additionalPrice > 0 && (
                <span className={css.VariantCardInfoPrice}>
                  +{formatMoney(variant.additionalPrice)} kr.
                </span>
              )}

            {props.priceDisplay === "salesPrice" && variant.salesPrice > 0 && (
              <span className={css.VariantCardInfoPrice}>
                +{formatMoney(variant.salesPrice)} kr.
              </span>
            )}
          </span>
          {props.categoryName && (
            <span className={css.VariantCardInfoCategoryName}>
              {props.categoryName}
            </span>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}

export type VariantLibraryPickerProps = {
  products: Array<Product>;
  variants: Array<BundleLineVariant>;
  categories: Array<Category>;
  pickingForVariantId: string;
  onPick?: (variantId: BundleLineVariantId, position: number) => void;
  priceDisplay?: PriceDisplay;
};
export function VariantLibraryPicker(props: VariantLibraryPickerProps) {
  const indexedProducts = useIndexBy(props.products, (p) => p.id);
  const indexedCategories = useIndexBy(props.categories, (c) => c.id);

  return (
    <div className={css.VariantLibraryPicker}>
      <div className={css.VariantLibraryContent}>
        {props.variants
          .filter((v) => v.id !== props.pickingForVariantId)
          .map((v, i) => {
            const product = indexedProducts.get(v.productId);
            const category = indexedCategories.get(
              product?.categories[0] ?? ""
            );
            const thumbSrc = ImageAsset.findUriWithSizeOrDefault(
              product?.coverAsset,
              "1x1-w100"
            );
            return (
              <VariantCard
                variant={v}
                thumbUrl={thumbSrc}
                categoryName={category?.name}
                priceDisplay={props.priceDisplay}
                onClick={() => {
                  props.onPick?.(v.id, i);
                }}
                {...staggerForwardsBackwards(
                  dropInOutFromBottom,
                  0.05,
                  i,
                  props.variants.length
                )}
              />
            );
          })}
      </div>
    </div>
  );
}
